import { useCallback, useState, useEffect } from 'react';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { parseMaskResultPromise } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useMaskedImage } from './use-masked-image';
import { VIEWS } from './constants';

export const useViewState = ({
  // original image info
  imageUrl,
  imageWidth,
  imageHeight,
  repository,
  perfMarker,
  logger,
}) => {
  const [activeView, setActiveView] = useState(VIEWS.BackgroundRemoval);
  const [isFetchingSegmentations, setIsFetchingSegmentations] = useState(false);
  const [fetchSegmentationError, setFetchSegmentationError] = useState('');
  const {
    imageUrlWithoutBackground,
    setImageUrlWithoutBackground,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    segmentationMasks,
    setSegmentationMasks,
    getMaskValueAtPoint,
    selectedMasks,
    setSelectedMasks,
    selectedImageSrc,
    setSelectedImageSrc,
    maskSetsBase64,
    setMaskSetsBase64,
    maskValues,
  } = useMaskedImage({
    imageUrl, imageWidth, imageHeight,
  });

  const removeImageBackground = useCallback(() => {
    const imageBackgroundRemovalPerfMarker = perfMarker.createChild('ImageBackgroundRemoval');
    imageBackgroundRemovalPerfMarker.willInit();
    const ret = repository.removeImageBackground(imageUrl).then(({ imageUrl: newImageUrl }) => {
      setImageUrlWithoutBackground(newImageUrl);
      logger.logTrace('Copilot change image background - removal image background successfully');
      return newImageUrl;
    }).finally(() => {
      imageBackgroundRemovalPerfMarker.done();
    });
    return ret;
  }, [imageUrl, perfMarker, repository, setImageUrlWithoutBackground, logger]);

  const getImageSegmentations = useCallback(() => {
    const imageSegmentationPerfMarker = perfMarker.createChild('ImageSegmentations');
    imageSegmentationPerfMarker.willInit();
    setIsFetchingSegmentations(true);
    const ret = repository.getImageSegmentations(imageUrl).then(result => (
      parseMaskResultPromise({
        result,
        imageUrl,
        imageWidth,
        imageHeight,
      })
    )).then((parsedResult) => {
      const {
        rawMaskSetsBase64String: rawMaskSetsBase64,
        maskSetsArray,
        selectedMasksArray,
        imageUrlWithoutBackground: selfDrawedImageUrl,
      } = parsedResult || {};
      setImageUrlWithoutBackground(selfDrawedImageUrl);
      setSegmentationMasks({ maskSetsArray, selectedMasksArray });
      setMaskSetsBase64(rawMaskSetsBase64);
      logger.logTrace('Copilot change image background - get image segmentations successfully', { originalUrl: imageUrl });
    }).catch((err) => {
      setFetchSegmentationError(err.uxMessage);
    })
      .finally(() => {
        setIsFetchingSegmentations(false);
        imageSegmentationPerfMarker.done();
      });
    return ret;
  }, [imageUrl, perfMarker, repository, setSegmentationMasks, logger, setImageUrlWithoutBackground, setMaskSetsBase64, imageWidth, imageHeight]);

  const generateImageBackground = useCallback((prompt) => {
    let finalImageUrl = imageUrl;
    if (selectedMasks === null) {
      // user doesn't select any objects, user the image url removed background
      finalImageUrl = imageUrlWithoutBackground;
    }
    const backgroundGenerationPerfMarker = perfMarker.createChild('ImageBackgroundGeneration');
    backgroundGenerationPerfMarker.willInit();
    const ret = repository.generateImageBackground({
      prompt,
      imageUrl: finalImageUrl,
      selectedMasks,
      maskSetsBase64,
      outputImageWidth: imageWidth,
      outputImageHeight: imageHeight,
    }).then((res) => {
      const { imageUrl: newImageUrl } = res || {};
      logger.logTrace('Copilot change image background - generate image background successfully', { generatedUrl: newImageUrl, prompt });
      setImageUrlWithGeneratedBackground(newImageUrl);
      return newImageUrl;
    }).finally(() => {
      backgroundGenerationPerfMarker.done();
    });
    return ret;
  }, [imageHeight, imageUrl, imageUrlWithoutBackground, imageWidth, perfMarker, repository, selectedMasks, setImageUrlWithGeneratedBackground, logger, maskSetsBase64]);

  useEffect(() => {
    getImageSegmentations();
  }, [getImageSegmentations]);

  useEffect(() => {
    logger.logTrace('Copilot change image background - User opened the change image background modal.');
  }, [logger]);

  useEffect(() => {
    setClarityTag({ OpenBackgroundRemovalDialog: true });
  }, []);

  return {
    // view states
    activeView,
    setActiveView,

    // APIs
    removeImageBackground,
    getImageSegmentations,
    isFetchingSegmentations,
    generateImageBackground,

    // image states
    imageUrlWithoutBackground,
    imageUrlWithGeneratedBackground,
    getMaskValueAtPoint,
    segmentationMasks,
    setSelectedImageSrc,
    selectedImageSrc,
    selectedMasks,
    setSelectedMasks,
    maskValues,
    fetchSegmentationError,
  };
};
